import { useState, useRef, Fragment } from "react";
import { ExclamationIcon } from "@heroicons/react/outline";
import { Dialog, Transition } from "@headlessui/react";
import axios from "axios";
import Notification from "../Notification";

export default function DeleteInstructorModal({ instructor_id, instructor_name, isOpen, setIsOpen, setRenderToggle }) {
    const cancelButtonRef = useRef(null);
    const [btnText, setBtnText] = useState("Yes, delete it permanently");
    const [showNotification, setShowNotification] = useState(false);
    const [notificationMessage, setNotificationMessage] = useState("");
    const [notificationType, setNotificationType] = useState("error");

    function handleClick(e) {
        e.stopPropagation();
        setBtnText("Deleting...");
        const headers = {
            "Content-Type": "text/plain",
        };
        
        axios
            .delete(`https://api.bollywoodfunknyc.com/instructors.php?instructor_id=${instructor_id}&force=true`, {
                headers,
            })
            .then((response) => {
                if (response.data.status === "success") {
                    setRenderToggle((renderToggle) => !renderToggle);
                    setIsOpen(false);
                    setNotificationType("success");
                    setNotificationMessage(response.data.message || "Instructor deleted successfully");
                    setShowNotification(true);
                } else {
                    setNotificationType("error");
                    setNotificationMessage(response.data.message || "Failed to delete instructor");
                    setShowNotification(true);
                }
                setTimeout(function () {
                    setBtnText("Yes, delete it permanently");
                }, 1000);
            })
            .catch((error) => {
                console.error("Error deleting instructor:", error);
                setNotificationType("error");
                setNotificationMessage(error.response?.data?.message || "Cannot delete instructor with check-ins");
                setShowNotification(true);
                setTimeout(function () {
                    setBtnText("Yes, delete it permanently");
                }, 1000);
            });
    }

    return (
        <>
            <Notification
                title={notificationType === "success" ? "Success" : "Error"}
                body={notificationMessage}
                show={showNotification}
                setShow={setShowNotification}
            />
            <Transition show={isOpen} as={Fragment}>
                <Dialog
                    as="div"
                    className="relative z-10"
                    initialFocus={cancelButtonRef}
                    onClose={() => {
                        setIsOpen(false);
                    }}
                >
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                    </Transition.Child>

                    <div className="fixed z-10 inset-0 overflow-y-auto">
                        <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
                                &#8203;
                            </span>
                            <Transition.Child
                                as={Fragment}
                                enter="ease-out duration-300"
                                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                                enterTo="opacity-100 translate-y-0 sm:scale-100"
                                leave="ease-in duration-200"
                                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            >
                                <div className="relative inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
                                    <div className="sm:flex sm:items-start">
                                        <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                                            <ExclamationIcon className="h-6 w-6 text-red-600" aria-hidden="true" />
                                        </div>
                                        <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                                            <h3 className="text-lg leading-6 font-medium text-gray-900">
                                                Permanently Delete Instructor
                                            </h3>
                                            <div className="mt-2">
                                                <p className="text-sm text-gray-500">
                                                    Are you sure you want to permanently delete <strong>{instructor_name}</strong>? This action cannot be
                                                    undone and is only possible if the instructor has no check-ins.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="mt-5 sm:mt-4 sm:ml-10 sm:pl-4 sm:flex">
                                        <button
                                            type="button"
                                            className="inline-flex justify-center w-full rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:w-auto sm:text-sm"
                                            onClick={handleClick}
                                        >
                                            {btnText}
                                        </button>
                                        <button
                                            type="button"
                                            className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 px-4 py-2 bg-white text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                                            onClick={() => {
                                                setIsOpen(false);
                                            }}
                                            ref={cancelButtonRef}
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </div>
                            </Transition.Child>
                        </div>
                    </div>
                </Dialog>
            </Transition>
        </>
    );
}